/*
 * Vesper shared components.
 * - Material Symbols loader (subset to the icons on the page)
 * - Scroll-in section animations
 * - <vesper-slider>: slide / loop / fade carousel with per-breakpoint options
 * - <vesper-countdown>: countdown timers (fixed date, evergreen, or daily reset)
 * - <quick-atc-button>: add a product to cart from any section
 * - <copy-to-clipboard>
 * - Store protection (disable copy / context menu / devtools shortcuts)
 */
(() => {
  const mobileQuery = window.matchMedia('(max-width: 749px)');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ---------- Material Symbols ---------- */
  const loadedIcons = new Set();
  let iconLink;
  function loadMaterialIcons(root = document) {
    const names = [...root.querySelectorAll('.material-icon[data-icon]')].map((el) => el.dataset.icon);
    const fresh = names.filter((n) => n && !loadedIcons.has(n));
    if (!fresh.length) return;
    fresh.forEach((n) => loadedIcons.add(n));
    const href =
      'https://fonts.googleapis.com/css2?family=Material+Symbols+Outlined:opsz,wght,FILL,GRAD@20..48,100..700,0..1,-50..200' +
      `&icon_names=${[...loadedIcons].sort().join(',')}&display=block`;
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    link.onload = () => {
      document.documentElement.classList.add('material-icons-ready');
      if (iconLink && iconLink !== link) iconLink.remove();
      iconLink = link;
    };
    document.head.appendChild(link);
  }

  /* ---------- Section animations ---------- */
  let animationObserver;
  function initAnimations(root = document) {
    const mode = document.body.dataset.animations;
    if (!mode) return;
    const targets = root.querySelectorAll('[data-animate]:not([data-animate-ready])');
    if (reducedMotion.matches || Shopify?.designMode) {
      targets.forEach((el) => el.setAttribute('data-animate-ready', 'in'));
      return;
    }
    animationObserver ??= new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const el = entry.target;
          if (entry.isIntersecting) {
            el.querySelectorAll('[data-animate-item]').forEach((item, i) => item.style.setProperty('--animate-index', i));
            el.setAttribute('data-animate-ready', 'in');
            if (mode !== 'repeat') animationObserver.unobserve(el);
          } else if (mode === 'repeat' && entry.boundingClientRect.top > 0) {
            el.setAttribute('data-animate-ready', 'out');
          }
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
    );
    targets.forEach((el) => {
      el.setAttribute('data-animate-ready', 'out');
      animationObserver.observe(el);
    });
  }

  /* ---------- Slider ---------- */
  const ARROW_SVG =
    '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M9 5l7 7-7 7" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  class VesperSlider extends HTMLElement {
    connectedCallback() {
      this.options = Object.assign(
        {
          type: 'slide',
          autoplay: false,
          interval: 3000,
          perPage: { desktop: 1, mobile: 1 },
          perMove: { desktop: 1, mobile: 1 },
          gap: { desktop: 0, mobile: 0 },
          padding: { desktop: 0, mobile: 0 },
          enabled: { desktop: true, mobile: true },
          arrows: { desktop: 'sides', mobile: 'under' },
          dots: { desktop: 'under', mobile: 'under' },
          adaptiveHeight: { desktop: false, mobile: false },
          pagination: 'dots',
          drag: true,
          pauseOnHover: true,
        },
        JSON.parse(this.dataset.options || '{}')
      );
      this.viewport = this.querySelector('.vslider__viewport');
      this.track = this.querySelector('.vslider__track');
      if (!this.viewport || !this.track) return;
      this.originals = [...this.track.children].filter((el) => !el.hasAttribute('data-clone'));
      this.index = 0;
      this.onResize = this.debounce(() => this.setup(), 150);
      this.onBreakpoint = () => this.setup();
      mobileQuery.addEventListener('change', this.onBreakpoint);
      window.addEventListener('resize', this.onResize);
      this.buildControls();
      this.bindDrag();
      this.setup();
      this.addEventListener('mouseenter', () => this.options.pauseOnHover && this.pause());
      this.addEventListener('mouseleave', () => this.options.pauseOnHover && this.play());
      this.addEventListener('focusin', () => this.pause());
      this.addEventListener('focusout', () => this.play());
      this.visibilityObserver = new IntersectionObserver(([e]) => (e.isIntersecting ? this.play() : this.pause()));
      this.visibilityObserver.observe(this);
      if (Shopify?.designMode) {
        document.addEventListener('shopify:block:select', (e) => {
          const i = this.originals.findIndex((s) => s.contains(e.target));
          if (i >= 0) {
            this.pause(true);
            this.goTo(i, true);
          }
        });
        document.addEventListener('shopify:block:deselect', () => this.play(true));
      }
    }

    disconnectedCallback() {
      mobileQuery.removeEventListener('change', this.onBreakpoint);
      window.removeEventListener('resize', this.onResize);
      this.visibilityObserver?.disconnect();
      clearInterval(this.timer);
    }

    bp(value) {
      if (value && typeof value === 'object' && ('desktop' in value || 'mobile' in value)) {
        return mobileQuery.matches ? value.mobile : value.desktop;
      }
      return value;
    }

    get isFade() {
      return this.options.type === 'fade';
    }
    get isLoop() {
      return this.options.type === 'loop' && !this.isFade;
    }

    setup() {
      const enabled = this.bp(this.options.enabled) !== false && this.originals.length > 0;
      this.perPage = Math.max(1, Number(this.bp(this.options.perPage)) || 1);
      this.perMove = Math.max(1, Math.min(Number(this.bp(this.options.perMove)) || 1, this.perPage));
      if (this.isFade) this.perPage = this.perMove = 1;
      this.style.setProperty('--per-page', this.perPage);
      this.style.setProperty('--slider-gap', `${Number(this.bp(this.options.gap)) || 0}px`);
      this.style.setProperty('--slider-padding', `${Number(this.bp(this.options.padding)) || 0}px`);
      this.removeClones();
      const active = enabled && this.originals.length > this.perPage;
      this.toggleAttribute('data-disabled', !enabled);
      this.toggleAttribute('data-static', enabled && !active);
      this.dataset.type = this.options.type;
      this.dataset.arrows = active ? this.bp(this.options.arrows) || 'hidden' : 'hidden';
      this.dataset.dots = active ? this.bp(this.options.dots) || 'hidden' : 'hidden';
      this.active = active;
      if (!active) {
        this.track.style.transform = '';
        this.viewport.style.height = '';
        clearInterval(this.timer);
        this.updateControls();
        return;
      }
      if (this.isLoop) this.addClones();
      this.index = Math.min(this.index, this.maxIndex());
      this.goTo(this.index, true);
      this.play();
    }

    addClones() {
      const n = this.perPage;
      const head = this.originals.slice(-n).map((el) => this.cloneSlide(el));
      const tail = this.originals.slice(0, n).map((el) => this.cloneSlide(el));
      head.reverse().forEach((c) => this.track.prepend(c));
      tail.forEach((c) => this.track.append(c));
      this.cloneOffset = n;
    }

    cloneSlide(el) {
      const c = el.cloneNode(true);
      c.setAttribute('data-clone', '');
      c.setAttribute('aria-hidden', 'true');
      c.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach((f) => f.setAttribute('tabindex', '-1'));
      c.querySelectorAll('[id]').forEach((f) => f.removeAttribute('id'));
      return c;
    }

    removeClones() {
      this.track.querySelectorAll(':scope > [data-clone]').forEach((c) => c.remove());
      this.cloneOffset = 0;
    }

    maxIndex() {
      if (this.isLoop || this.isFade) return this.originals.length - 1;
      return Math.max(0, this.originals.length - this.perPage);
    }

    pageCount() {
      if (this.isLoop || this.isFade) return Math.ceil(this.originals.length / this.perMove);
      return Math.ceil(this.maxIndex() / this.perMove) + 1;
    }

    slideWidth() {
      const first = this.originals[0];
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      return first.getBoundingClientRect().width + gap;
    }

    goTo(target, instant = false) {
      if (!this.active) return;
      const total = this.originals.length;
      let index = target;
      if (this.isFade) {
        index = ((target % total) + total) % total;
      } else if (this.isLoop) {
        // Allow moving one step into the clones, then jump back after the transition.
        index = Math.max(-this.perPage, Math.min(target, total));
      } else if (this.options.type === 'slide' && !instant && target > this.maxIndex() && this.index >= this.maxIndex()) {
        index = 0; // rewind at the end
      } else if (target < 0 && this.index === 0) {
        index = this.maxIndex();
      } else {
        index = Math.max(0, Math.min(target, this.maxIndex()));
      }
      this.index = index;
      if (this.isFade) {
        this.originals.forEach((s, i) => s.classList.toggle('is-active', i === index));
      } else {
        const offset = (index + this.cloneOffset) * this.slideWidth();
        this.track.style.transition = instant ? 'none' : '';
        this.track.style.transform = `translate3d(${-offset}px, 0, 0)`;
        if (instant) this.track.getBoundingClientRect();
        if (this.isLoop && (index < 0 || index >= total)) {
          const normal = ((index % total) + total) % total;
          const done = () => {
            this.track.removeEventListener('transitionend', done);
            this.index = normal;
            this.goTo(normal, true);
          };
          if (instant) done();
          else this.track.addEventListener('transitionend', done);
        }
      }
      this.updateSlides();
      this.updateControls();
      this.updateHeight();
      this.dispatchEvent(new CustomEvent('slider:change', { bubbles: true, detail: { index: this.realIndex() } }));
    }

    realIndex() {
      const total = this.originals.length;
      return ((this.index % total) + total) % total;
    }

    updateSlides() {
      const start = this.realIndex();
      this.originals.forEach((s, i) => {
        const offset = (i - start + this.originals.length) % this.originals.length;
        const visible = offset < this.perPage;
        s.toggleAttribute('data-visible', visible);
        s.setAttribute('aria-hidden', visible ? 'false' : 'true');
      });
    }

    updateHeight() {
      if (!this.bp(this.options.adaptiveHeight)) {
        this.viewport.style.height = '';
        return;
      }
      const slides = this.originals.filter((s) => s.hasAttribute('data-visible'));
      const h = Math.max(...slides.map((s) => s.scrollHeight));
      if (h > 0) this.viewport.style.height = `${h}px`;
    }

    next() {
      this.goTo(this.index + this.perMove);
    }
    prev() {
      this.goTo(this.index - this.perMove);
    }

    play(force) {
      if (force) this.forcePaused = false;
      clearInterval(this.timer);
      if (!this.active || !this.options.autoplay || this.forcePaused || reducedMotion.matches) return;
      this.timer = setInterval(() => this.next(), Math.max(1000, Number(this.options.interval) || 3000));
    }
    pause(force) {
      if (force) this.forcePaused = true;
      clearInterval(this.timer);
    }

    buildControls() {
      const labels = window.accessibilityStrings || {};
      this.arrowWrap = this.querySelector('.vslider__arrows');
      if (!this.arrowWrap) {
        this.arrowWrap = document.createElement('div');
        this.arrowWrap.className = 'vslider__arrows';
        this.arrowWrap.innerHTML =
          `<button type="button" class="vslider__arrow vslider__arrow--prev" aria-label="${labels.previousSlide || 'Previous slide'}">${ARROW_SVG}</button>` +
          `<button type="button" class="vslider__arrow vslider__arrow--next" aria-label="${labels.nextSlide || 'Next slide'}">${ARROW_SVG}</button>`;
        this.append(this.arrowWrap);
      }
      this.arrowWrap.querySelector('.vslider__arrow--prev')?.addEventListener('click', () => this.prev());
      this.arrowWrap.querySelector('.vslider__arrow--next')?.addEventListener('click', () => this.next());
      this.dotWrap = this.querySelector('.vslider__dots');
      if (!this.dotWrap) {
        this.dotWrap = document.createElement('div');
        this.dotWrap.className = 'vslider__dots';
        this.append(this.dotWrap);
      }
      this.dotWrap.addEventListener('click', (e) => {
        const dot = e.target.closest('[data-page]');
        if (dot) this.goTo(Number(dot.dataset.page) * this.perMove);
      });
      this.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') this.prev();
        if (e.key === 'ArrowRight') this.next();
      });
    }

    updateControls() {
      if (!this.dotWrap) return;
      const pages = this.active ? this.pageCount() : 0;
      const current = Math.min(Math.round(this.realIndex() / this.perMove), Math.max(0, pages - 1));
      const style = this.options.pagination;
      this.dataset.pagination = style;
      if (style === 'counter' || style === 'numeric') {
        this.dotWrap.innerHTML = pages ? `<span class="vslider__counter">${current + 1}/${pages}</span>` : '';
      } else if (this.dotWrap.children.length !== pages || this.dotWrap.dataset.style !== style) {
        this.dotWrap.dataset.style = style;
        this.dotWrap.innerHTML = Array.from(
          { length: pages },
          (_, i) =>
            `<button type="button" class="vslider__dot" data-page="${i}" aria-label="${i + 1} / ${pages}">${style === 'numbers' ? i + 1 : ''}</button>`
        ).join('');
      }
      [...this.dotWrap.querySelectorAll('.vslider__dot')].forEach((d, i) => d.setAttribute('aria-current', i === current ? 'true' : 'false'));
      if (!this.isLoop && !this.isFade && this.options.type !== 'slide') {
        this.arrowWrap.querySelector('.vslider__arrow--prev').disabled = this.index <= 0;
        this.arrowWrap.querySelector('.vslider__arrow--next').disabled = this.index >= this.maxIndex();
      }
    }

    bindDrag() {
      if (this.options.drag === false || this.options.drag === 'disabled') return;
      let startX = 0, startY = 0, dx = 0, dragging = false, locked = null, base = 0;
      const down = (e) => {
        if (!this.active || this.isFade && e.pointerType === 'mouse') return;
        if (e.target.closest('input, select, textarea, .vslider__arrow, .vslider__dot')) return;
        dragging = true;
        locked = null;
        startX = e.clientX;
        startY = e.clientY;
        dx = 0;
        base = (this.index + this.cloneOffset) * this.slideWidth();
        this.pause();
      };
      const move = (e) => {
        if (!dragging) return;
        dx = e.clientX - startX;
        const dy = e.clientY - startY;
        if (locked === null && (Math.abs(dx) > 6 || Math.abs(dy) > 6)) locked = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
        if (locked !== 'x') return;
        e.preventDefault();
        this.setAttribute('data-dragging', '');
        if (!this.isFade) {
          this.track.style.transition = 'none';
          this.track.style.transform = `translate3d(${-base + dx}px, 0, 0)`;
        }
      };
      const up = () => {
        if (!dragging) return;
        dragging = false;
        this.removeAttribute('data-dragging');
        this.track.style.transition = '';
        if (locked === 'x' && Math.abs(dx) > 40) {
          const steps = this.options.drag === 'free' ? Math.max(1, Math.round(Math.abs(dx) / this.slideWidth())) : this.perMove;
          this.goTo(this.index + (dx < 0 ? steps : -steps));
        } else {
          this.goTo(this.index);
        }
        this.play();
      };
      this.viewport.addEventListener('pointerdown', down);
      window.addEventListener('pointermove', move, { passive: false });
      window.addEventListener('pointerup', up);
      window.addEventListener('pointercancel', up);
      this.viewport.addEventListener('click', (e) => {
        if (Math.abs(dx) > 6) {
          e.preventDefault();
          e.stopPropagation();
          dx = 0;
        }
      }, true);
      this.viewport.addEventListener('dragstart', (e) => e.preventDefault());
    }

    debounce(fn, wait) {
      let t;
      return (...args) => {
        clearTimeout(t);
        t = setTimeout(() => fn(...args), wait);
      };
    }
  }
  if (!customElements.get('vesper-slider')) customElements.define('vesper-slider', VesperSlider);

  /* ---------- Countdown ---------- */
  // data-end: ISO date | data-duration: minutes (evergreen, per visitor) | data-daily: "HH:MM" reset time
  class VesperCountdown extends HTMLElement {
    connectedCallback() {
      this.end = this.resolveEnd();
      this.parts = {
        days: this.querySelector('[data-days]'),
        hours: this.querySelector('[data-hours]'),
        minutes: this.querySelector('[data-minutes]'),
        seconds: this.querySelector('[data-seconds]'),
      };
      this.tick();
      this.timer = setInterval(() => this.tick(), 1000);
    }
    disconnectedCallback() {
      clearInterval(this.timer);
    }
    resolveEnd() {
      const { end, duration, daily, key } = this.dataset;
      if (end) return new Date(end).getTime();
      if (daily) {
        const [h, m] = daily.split(':').map(Number);
        const d = new Date();
        d.setHours(h || 0, m || 0, 0, 0);
        if (d.getTime() <= Date.now()) d.setDate(d.getDate() + 1);
        return d.getTime();
      }
      if (duration) {
        const storageKey = `vesper-countdown-${key || this.id || 'default'}`;
        const ms = Number(duration) * 60000;
        let stored = 0;
        try {
          stored = Number(localStorage.getItem(storageKey)) || 0;
        } catch (e) {}
        if (!stored || stored < Date.now()) {
          stored = Date.now() + ms;
          try {
            localStorage.setItem(storageKey, String(stored));
          } catch (e) {}
        }
        return stored;
      }
      return Date.now();
    }
    tick() {
      let left = Math.max(0, this.end - Date.now());
      if (left === 0 && this.dataset.duration && this.dataset.restart !== 'false') {
        try {
          localStorage.removeItem(`vesper-countdown-${this.dataset.key || this.id || 'default'}`);
        } catch (e) {}
        this.end = this.resolveEnd();
        left = Math.max(0, this.end - Date.now());
      }
      const s = Math.floor(left / 1000);
      const values = { days: Math.floor(s / 86400), hours: Math.floor((s % 86400) / 3600), minutes: Math.floor((s % 3600) / 60), seconds: s % 60 };
      if (!this.parts.days) values.hours += values.days * 24;
      for (const [k, el] of Object.entries(this.parts)) {
        if (!el) continue;
        const text = String(values[k]).padStart(2, '0');
        if (el.hasAttribute('data-split')) {
          el.innerHTML = [...text].map((c) => `<span class="countdown__digit">${c}</span>`).join('');
        } else {
          el.textContent = text;
        }
      }
      this.toggleAttribute('data-expired', left === 0);
      if (left === 0) {
        clearInterval(this.timer);
        if (this.dataset.hideExpired === 'true') this.closest('[data-countdown-container]')?.setAttribute('hidden', '');
      }
    }
  }
  if (!customElements.get('vesper-countdown')) customElements.define('vesper-countdown', VesperCountdown);

  /* ---------- Quick add-to-cart button ---------- */
  // <quick-atc-button data-variant-id="123" data-skip-cart="true"><button>…</button></quick-atc-button>
  class QuickAtcButton extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('button');
      this.button?.addEventListener('click', (e) => this.add(e));
    }
    async add(event) {
      event.preventDefault();
      const id = this.dataset.variantId;
      if (!id || this.button.getAttribute('aria-disabled') === 'true') return;
      const skipCart = this.dataset.skipCart === 'true';
      const quantity = Number(this.dataset.quantity) || 1;
      this.button.setAttribute('aria-disabled', 'true');
      this.button.classList.add('loading');
      const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
      const sections = cart && !skipCart && cart.getSectionsToRender ? cart.getSectionsToRender().map((s) => s.id) : [];
      try {
        const res = await fetch(`${window.routes.cart_add_url}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: Number(id), quantity }], sections, sections_url: window.location.pathname }),
        });
        const data = await res.json();
        if (!res.ok || data.status) throw new Error(data.description || data.message || window.cartStrings?.error || 'Error');
        if (skipCart) {
          window.location.href = '/checkout';
          return;
        }
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'quick-atc-button', productVariantId: id, cartData: data });
        }
        if (cart && cart.renderContents) {
          const item = data.items ? data.items[0] : data;
          cart.renderContents({ ...item, sections: data.sections });
        } else {
          window.location.href = window.routes.cart_url;
        }
      } catch (err) {
        console.error(err);
        this.button.classList.add('quick-atc--error');
        setTimeout(() => this.button.classList.remove('quick-atc--error'), 2000);
      } finally {
        this.button.removeAttribute('aria-disabled');
        this.button.classList.remove('loading');
      }
    }
  }
  if (!customElements.get('quick-atc-button')) customElements.define('quick-atc-button', QuickAtcButton);

  /* ---------- Copy to clipboard ---------- */
  class CopyToClipboard extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', async (e) => {
        e.preventDefault();
        try {
          await navigator.clipboard.writeText(this.dataset.copy || '');
          this.setAttribute('data-copied', '');
          setTimeout(() => this.removeAttribute('data-copied'), 2000);
        } catch (err) {}
      });
    }
  }
  if (!customElements.get('copy-to-clipboard')) customElements.define('copy-to-clipboard', CopyToClipboard);

  /* ---------- Store protection ---------- */
  function initProtection() {
    if (!document.body.hasAttribute('data-protect')) return;
    const editable = (t) => t.closest && t.closest('input, textarea, [contenteditable="true"]');
    document.addEventListener('contextmenu', (e) => !editable(e.target) && e.preventDefault());
    document.addEventListener('copy', (e) => !editable(e.target) && e.preventDefault());
    document.addEventListener('dragstart', (e) => e.target.tagName === 'IMG' && e.preventDefault());
    document.addEventListener('keydown', (e) => {
      const k = e.key.toLowerCase();
      const blocked = k === 'f12' || ((e.ctrlKey || e.metaKey) && (k === 'u' || k === 's' || (e.shiftKey && ['i', 'j', 'c'].includes(k))));
      if (blocked) e.preventDefault();
    });
  }

  /* ---------- License ----------
   * Keys are issued per store with tools/license.js and entered in Theme settings > License.
   * A key is "STRATA1.<payload>.<signature>": an ECDSA P-256 signature over the payload, which names
   * the store's permanent *.myshopify.com domain and an optional expiry date.
   * - Published theme without a valid key: the storefront is blocked.
   * - Theme editor and unpublished previews: a notice only, so the theme can be set up before licensing.
   * - Expired keys keep working for LICENSE_GRACE_DAYS with a notice in the editor.
   * - If the browser can't verify signatures, nothing is blocked. */
  const LICENSE_PUBLIC_KEY = 'MFkwEwYHKoZIzj0CAQYIKoZIzj0DAQcDQgAEO4eORqaD4F8aEnK8Fg0iW75wSle+80JGhMmBDw59IE82sRvbpeI8EVBkfmNyDzXL9mYLQmKqGPGhoydpXjtp5Q==';
  const LICENSE_GRACE_DAYS = 14;
  const LICENSE_CONTACT_URL = 'https://shyftstack.com/contact/';

  const b64urlBytes = (s) => {
    const bin = atob(s.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(s.length / 4) * 4, '='));
    return Uint8Array.from(bin, (c) => c.charCodeAt(0));
  };

  async function verifyLicense(key, shop) {
    if (!LICENSE_PUBLIC_KEY || !window.crypto?.subtle) return { status: 'unverifiable' };
    if (!key) return { status: 'missing' };
    const parts = key.split('.');
    if (parts.length !== 3 || parts[0] !== 'STRATA1') return { status: 'invalid' };
    try {
      const publicKey = await crypto.subtle.importKey('spki', b64urlBytes(LICENSE_PUBLIC_KEY), { name: 'ECDSA', namedCurve: 'P-256' }, false, ['verify']);
      const valid = await crypto.subtle.verify(
        { name: 'ECDSA', hash: 'SHA-256' },
        publicKey,
        b64urlBytes(parts[2]),
        new TextEncoder().encode(`${parts[0]}.${parts[1]}`)
      );
      if (!valid) return { status: 'invalid' };
      const payload = JSON.parse(new TextDecoder().decode(b64urlBytes(parts[1])));
      if (String(payload.shop).toLowerCase() !== shop) return { status: 'wrong-shop', payload };
      if (payload.exp) {
        const end = Date.parse(payload.exp) + 86400000;
        if (Date.now() > end + LICENSE_GRACE_DAYS * 86400000) return { status: 'expired', payload };
        if (Date.now() > end) return { status: 'grace', payload };
      }
      return { status: 'valid', payload };
    } catch (e) {
      return { status: 'invalid' };
    }
  }

  function licenseMessage(result, shop) {
    switch (result.status) {
      case 'missing':
        return `No Strata license key is set for ${shop}.`;
      case 'wrong-shop':
        return `This Strata license key belongs to ${result.payload.shop}, not ${shop}. Each store needs its own license.`;
      case 'expired':
        return `The Strata license for ${shop} expired on ${result.payload.exp}.`;
      case 'grace':
        return `The Strata license for ${shop} expired on ${result.payload.exp}. Renew it within ${LICENSE_GRACE_DAYS} days of that date to keep the store online.`;
      default:
        return 'The Strata license key is not valid.';
    }
  }

  function showLicenseNotice(text, blocking) {
    if (document.querySelector('.strata-license')) return;
    const box = document.createElement('div');
    box.className = `strata-license${blocking ? ' strata-license--block' : ''}`;
    box.setAttribute('role', blocking ? 'alertdialog' : 'status');
    const unavailable = document.body.dataset.licenseUnavailable || 'This store is temporarily unavailable.';
    box.innerHTML = blocking
      ? `<div class="strata-license__panel"><p class="strata-license__title"></p><p class="strata-license__detail"></p><a class="strata-license__link" href="${LICENSE_CONTACT_URL}" rel="noopener">Store owner? Contact Shyft Stack</a></div>`
      : `<p class="strata-license__detail"></p><a class="strata-license__link" href="${LICENSE_CONTACT_URL}" target="_blank" rel="noopener">Get a license</a><button type="button" class="strata-license__close" aria-label="Close">×</button>`;
    if (blocking) box.querySelector('.strata-license__title').textContent = unavailable;
    box.querySelector('.strata-license__detail').textContent = text;
    box.querySelector('.strata-license__close')?.addEventListener('click', () => box.remove());
    document.body.appendChild(box);
    if (blocking) document.documentElement.classList.add('strata-locked');
  }

  async function checkLicense() {
    const body = document.body;
    const shop = (body.dataset.shop || '').toLowerCase();
    const result = await verifyLicense((body.dataset.license || '').trim(), shop);
    if (result.status === 'valid' || result.status === 'unverifiable') return;
    const text = licenseMessage(result, shop);
    console.warn(`[Strata] ${text}`);
    const preview = Shopify?.designMode || window.Shopify?.theme?.role !== 'main';
    if (preview || result.status === 'grace') {
      if (preview) showLicenseNotice(`${text} The storefront will be blocked when this theme is published without a valid license.`, false);
      return;
    }
    showLicenseNotice(text, true);
  }

  /* ---------- Boot ---------- */
  function init(root = document) {
    loadMaterialIcons(root);
    initAnimations(root);
  }
  window.Vesper = { init, loadMaterialIcons, initAnimations };
  init();
  initProtection();
  checkLicense();
  document.addEventListener('shopify:section:load', (e) => init(e.target));
  // Content injected later (cart drawer, quick add, predictive search) may contain icons.
  new MutationObserver((records) => {
    if (records.some((r) => [...r.addedNodes].some((n) => n.nodeType === 1 && (n.matches?.('.material-icon') || n.querySelector?.('.material-icon'))))) {
      loadMaterialIcons();
    }
  }).observe(document.body, { childList: true, subtree: true });
})();
